import React, { createContext, useContext, useState, useEffect } from 'react';

const API_BASE = '';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(() => typeof localStorage === 'undefined' ? null : localStorage.getItem('accessToken'));
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const init = async () => {
      // If we already have an access token, fetch the user.
      // fetchCurrentUser will automatically try attemptTrustedRefresh on 401.
      if (accessToken) {
        await fetchCurrentUser();
        return;
      }
      // No access token — try to recover from a stored trusted session.
      const recovered = await attemptTrustedRefresh();
      if (!recovered) {
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Re-fetch the user whenever accessToken changes after mount (login, trusted refresh)
    if (accessToken && !user) {
      fetchCurrentUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        let bodyText = '';
        try { bodyText = await response.text(); } catch (_) {}
        let userData = null;
        try { userData = JSON.parse(bodyText); } catch (_) {}
        if (userData) {
          setUser(userData);
        } else {
          await softLogout();
        }
      } else {
        // Access token invalid/expired. Try trusted-session refresh before giving up.
        const recovered = await attemptTrustedRefresh();
        if (!recovered) {
          await softLogout();
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      await softLogout();
    } finally {
      setLoading(false);
    }
  };

  // Attempt to swap a stored trustedSession refreshToken for a fresh access token.
  // Returns true if successful. Safari-safe response parsing.
  const attemptTrustedRefresh = async () => {
    const stored = localStorage.getItem('trustedSession');
    if (!stored) return false;
    try {
      const { refreshToken: storedRefresh, expiresAt } = JSON.parse(stored);
      if (!storedRefresh || new Date(expiresAt) < new Date()) {
        localStorage.removeItem('trustedSession');
        return false;
      }
      const resp = await fetch(`${API_URL}/api/auth/sessions/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: storedRefresh }),
      });
      let bodyText = '';
      try { bodyText = await resp.text(); } catch (_) {}
      let data = {};
      try { data = JSON.parse(bodyText); } catch (_) {}
      if (resp.ok && data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        setAccessToken(data.accessToken);
        // Immediately fetch user with the new token
        const meResp = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${data.accessToken}` },
        });
        if (meResp.ok) {
          let meText = '';
          try { meText = await meResp.text(); } catch (_) {}
          try {
            const me = JSON.parse(meText);
            if (me) setUser(me);
            return true;
          } catch (_) { /* fall through */ }
        }
      } else if (resp.status === 401) {
        // Session revoked or invalid server-side — safe to drop.
        localStorage.removeItem('trustedSession');
      }
      return false;
    } catch {
      return false;
    }
  };

  // Clear only access/refresh tokens, leave trustedSession alone so the
  // user's "Remember this device" anchor survives access-token expiry.
  const softLogout = async () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setAccessToken(null);
    setUser(null);
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    // Read body as text first (monitoring scripts may consume body stream)
    let bodyText = '';
    try { bodyText = await response.text(); } catch (_) {}
    let data = {};
    try { data = JSON.parse(bodyText); } catch (_) {}

    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }

    // Check if 2FA is required
    if (data.requires2FA) {
      return { requires2FA: true, twoFactorToken: data.twoFactorToken };
    }

    // Check if mandatory 2FA enrollment is required
    if (data.requires2FASetup) {
      return { requires2FASetup: true, twoFactorToken: data.twoFactorToken, message: data.message };
    }

    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setAccessToken(data.accessToken);
    setUser(data.user);
    
    return data.user;
  };

  const verify2FA = async (twoFactorToken, code) => {
    const response = await fetch(`${API_BASE}/api/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ twoFactorToken, code })
    });

    let bodyText = '';
    try { bodyText = await response.text(); } catch (_) {}
    let data = {};
    try { data = JSON.parse(bodyText); } catch (_) {}

    if (!response.ok) {
      throw new Error(data.detail || 'Verification failed');
    }

    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setAccessToken(data.accessToken);
    setUser(data.user);

    return data.user;
  };

  const enrollSetup2FA = async (twoFactorToken) => {
    const response = await fetch(`${API_BASE}/api/auth/2fa/enroll-setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ twoFactorToken })
    });
    let bodyText = '';
    try { bodyText = await response.text(); } catch (_) {}
    let data = {};
    try { data = JSON.parse(bodyText); } catch (_) {}
    if (!response.ok) throw new Error(data.detail || 'Setup failed');
    return data;
  };

  const enrollVerify2FA = async (twoFactorToken, code) => {
    const response = await fetch(`${API_BASE}/api/auth/2fa/enroll-verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ twoFactorToken, code })
    });
    let bodyText = '';
    try { bodyText = await response.text(); } catch (_) {}
    let data = {};
    try { data = JSON.parse(bodyText); } catch (_) {}
    if (!response.ok) throw new Error(data.detail || 'Verification failed');
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  };

  // Auto-refresh from trusted session is handled in the mount useEffect above.

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('trustedSession');
    setAccessToken(null);
    setUser(null);
  };

  const isAdmin = () => {
    return user && ['super_admin', 'admin'].includes(user.role);
  };

  const isInternal = () => {
    return user && ['super_admin', 'admin', 'internal_team', 'read_only'].includes(user.role);
  };

  // Read-only internal users can view but not create/modify
  const canCreateDeals = () => {
    return user && ['super_admin', 'admin', 'internal_team'].includes(user.role);
  };

  const canBid = () => {
    return user && ['super_admin', 'admin', 'lender', 'family_office'].includes(user.role);
  };

  const refreshUser = async () => {
    if (accessToken) {
      await fetchCurrentUser();
    }
  };

  const setAuthFromInvite = (userData, token) => {
    setAccessToken(token);
    setUser(userData);
  };

  const value = {
    user,
    loading,
    accessToken,
    login,
    verify2FA,
    enrollSetup2FA,
    enrollVerify2FA,
    logout,
    isAdmin,
    isInternal,
    canCreateDeals,
    canBid,
    refreshUser,
    setAuthFromInvite,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
